import { useMemo, useState, useEffect } from "react";
import { useCrm } from "@/lib/crm";
import { useAuth } from "@/lib/auth";
import { eur2 } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Save, Trash2, Tag, Lock } from "lucide-react";

export default function Listino() {
  const { settings, api, setSettings } = useCrm();
  const { isTitolare } = useAuth();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("");

  useEffect(() => { if (settings) setItems(settings.listino || []); }, [settings]);

  const categories = useMemo(() => {
    const set = [...new Set(items.map((i) => i.categoria))];
    return set.length ? set : ["Generale"];
  }, [items]);

  useEffect(() => {
    if (categories.length && !categories.includes(active)) setActive(categories[0]);
  }, [categories, active]);

  const update = (id, k, v) => setItems((arr) => arr.map((it) => (it.id === id ? { ...it, [k]: v } : it)));
  const removeItem = (id) => setItems((arr) => arr.filter((it) => it.id !== id));
  const addItem = (cat) => setItems((arr) => [...arr, { id: `new-${Date.now()}`, categoria: cat, nome: "Nuova voce", prezzo: 0, descrizione: "" }]);

  const save = async () => {
    const clean = items.map(({ id, ...rest }) => ({ ...rest, prezzo: Number(rest.prezzo) || 0 }));
    const s = await api.updateSettings({ listino: clean });
    setSettings(s);
    setItems(s.listino);
    toast.success("Listino salvato");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">Listino Prezzi</h1>
          <p className="text-slate-500 mt-1">Riferimento ufficiale. Solo il titolare può modificare i prezzi.</p>
        </div>
        <Button data-testid="save-listino-button" style={{ display: isTitolare ? undefined : "none" }} onClick={save} className="rounded-full gap-1.5"><Save className="h-4 w-4" /> Salva listino</Button>
      </div>

      <Card className="p-3 bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-800/60 flex items-center gap-2 text-sm text-amber-800 dark:text-amber-300 animate-fade-up">
        <Lock className="h-4 w-4 flex-shrink-0" />
        I collaboratori NON sono autorizzati a modificare prezzi, promettere sconti o definire condizioni personalizzate.
      </Card>

      <Tabs value={active} onValueChange={setActive} className="w-full">
        <TabsList className="flex-wrap h-auto">
          {categories.map((cat) => (
            <TabsTrigger key={cat} value={cat} data-testid="listino-category-tab">{cat}</TabsTrigger>
          ))}
        </TabsList>

        {categories.map((cat) => (
          <TabsContent key={cat} value={cat} className="space-y-3 mt-4">
            {items.filter((i) => i.categoria === cat).map((it) => (
              <Card key={it.id} className="p-4 flex items-center gap-3 animate-fade-up">
                <Tag className="h-4 w-4 text-primary flex-shrink-0" />
                <div className="grid sm:grid-cols-[1fr,2fr,auto] gap-3 flex-1 items-center">
                  <Input value={it.nome} onChange={(e) => update(it.id, "nome", e.target.value)} placeholder="Nome voce" className="font-medium" />
                  <Input value={it.descrizione} onChange={(e) => update(it.id, "descrizione", e.target.value)} placeholder="Descrizione" />
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Input type="number" value={it.prezzo} onChange={(e) => update(it.id, "prezzo", e.target.value)} className="w-28 pr-7 text-right tabular-nums" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                    </div>
                    <button onClick={() => removeItem(it.id)} className="text-slate-300 hover:text-destructive transition-colors"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              </Card>
            ))}
            <Button variant="outline" onClick={() => addItem(cat)} className="rounded-full gap-1.5 border-dashed">
              <Plus className="h-4 w-4" /> Aggiungi voce a "{cat}"
            </Button>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
